import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Autocomplete,
  createFilterOptions,
  InputAdornment,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import utc from "dayjs/plugin/utc";
import { HtmlTooltip } from "components/HeatMap";
import { CustomPopper } from "components/CustomPopper";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxIcon from "@mui/icons-material/CheckBoxRounded";
import { useAuth } from "../../../contexts/AuthContext";
import { getBorderColor } from "../interpretations/InterpretationNewEdit";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";

const TranslationNewEdit = () => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { setCurrentEntity } = usePresence();

  const filter = createFilterOptions();

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    triggerPageRefresh,
    openDialog,
    closeDialog,
  } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_NEW
      ? "New Translation"
      : "Edit Translation",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  // State for paid assignment warning dialog
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningMessage, setPaidWarningMessage] = useState("");
  const [pendingSave, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_NEW;

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      setCurrentEntity({
        entityType: "Translation",
        entityId: overlayData.id,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // Reload the overlay data with fresh data from server
    api
      .get(`/services/translations/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        triggerPageRefresh("Assignment data refreshed", theme);
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "Translation",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const [, setAddNewClient] = useState(false);
  const [, setAddNewRequestor] = useState(false);
  const [, setAddNewInvoiceTo] = useState(false);

  const completing =
    overlayData.Action === OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE;

  const [name, setName] = useState(isNewAssignment ? "" : overlayData.Name);

  const [date, setDate] = useState(
    isNewAssignment ? dayjs() : dayjs(overlayData.Date),
  );

  const [deadline, setDeadline] = useState(
    isNewAssignment ? null : dayjs(overlayData.Deadline),
  );

  const [deliveryDate, setDeliveryDate] = useState(
    isNewAssignment ? null : dayjs(overlayData.DeliveryDate),
  );

  const [projectNumber, setProjectNumber] = useState(
    overlayData.ProjectNumber || "",
  );

  const [translationType, setTranslationType] = useState(
    isNewAssignment ? "Translation" : overlayData.TranslationType,
  );

  const [client, setClient] = useState(
    isNewAssignment ? "" : overlayData.Client,
  );

  const [requestor, setRequestor] = useState(
    isNewAssignment ? "" : overlayData.Requestor,
  );

  const [invoiceTo, setInvoiceTo] = useState(
    isNewAssignment ? "" : overlayData.Invoicer,
  );

  const [sourceLanguage, setSourceLanguage] = useState(
    isNewAssignment
      ? overlayData.Languages?.find((l) => l.Name === "English") || {
          Name: "N/A",
        }
      : overlayData.Language,
  );

  const assignmentTargetLanguages =
    overlayData.ServiceTargetLanguages &&
    overlayData.ServiceTargetLanguages.map((language) => ({
      id: language.LanguageID,
      Name: language.Language.Name,
    }));

  const [targetLanguages, setTargetLanguages] = useState(
    assignmentTargetLanguages || [],
  );

  const [unitTypes, setUnitTypes] = useState([]);

  const [unitTypeId, setUnitTypeId] = useState(
    isNewAssignment
      ? ""
      : overlayData.UnitTypeID || overlayData.UnitType?.id || "",
  );

  const [unitCount, setUnitCount] = useState(
    isNewAssignment ? "" : (overlayData.UnitCount ?? overlayData.WordCount),
  );

  useEffect(() => {
    let isMounted = true;

    api
      .get("/unitTypes")
      .then((response) => {
        if (!isMounted) return;
        setUnitTypes(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching unit types:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!unitTypes.length) return;

    if (unitTypeId) return;

    if (!isNewAssignment && overlayData.UnitTypeID) {
      setUnitTypeId(overlayData.UnitTypeID);
      return;
    }

    const defaultUnit = unitTypes.find((unit) => unit.Name === "Word");
    if (defaultUnit) {
      setUnitTypeId(defaultUnit.id);
    } else if (unitTypes[0]) {
      setUnitTypeId(unitTypes[0].id);
    }
  }, [unitTypes, unitTypeId, isNewAssignment, overlayData.UnitTypeID]);

  const selectedUnitType = unitTypes.find(
    (unit) => unit.id === Number(unitTypeId),
  );
  const unitTypeName = selectedUnitType?.Name?.toLowerCase() || "unit";
  const unitTypeAbbrev = selectedUnitType?.Abbreviation || unitTypeName;
  const translationUnitTypes = unitTypes.filter((unit) => unit.Name !== "Hour");

  const [numberOfLanguages, setNumberOfLanguages] = useState(
    isNewAssignment
      ? 1
      : overlayData.NumberOfLanguages || targetLanguages.length,
  );

  const englishLanguage = overlayData.Languages?.find(
    (language) => language.Name === "English",
  );

  const getLanguageKey = (language) => {
    if (!language) return "";

    if (language.id != null) {
      return `id:${language.id}`;
    }

    return `name:${(language.Name || "").trim().toLowerCase()}`;
  };

  const isEnglishLanguage = (language) =>
    (language?.Name || "").trim().toLowerCase() === "english";

  const normalizeTargetLanguages = (languages, selectedSourceLanguage) => {
    const sourceKey = getLanguageKey(selectedSourceLanguage);
    const uniqueLanguages = [];
    const seenKeys = new Set();

    (languages || []).forEach((language) => {
      const languageKey = getLanguageKey(language);

      if (
        !languageKey ||
        languageKey === sourceKey ||
        seenKeys.has(languageKey)
      ) {
        return;
      }

      seenKeys.add(languageKey);
      uniqueLanguages.push(language);
    });

    if (
      selectedSourceLanguage &&
      !isEnglishLanguage(selectedSourceLanguage) &&
      englishLanguage
    ) {
      const englishKey = getLanguageKey(englishLanguage);

      if (!seenKeys.has(englishKey)) {
        uniqueLanguages.unshift({
          id: englishLanguage.id,
          Name: englishLanguage.Name,
        });
      }
    }

    return uniqueLanguages;
  };

  const [deliveryType, setDeliveryType] = useState(
    isNewAssignment ? "" : overlayData.DeliveryType,
  );

  const [rateType, setRateType] = useState(
    isNewAssignment ? "Translation" : overlayData.RateType || "Translation",
  );

  const [paymentType, setPaymentType] = useState(
    isNewAssignment ? "" : overlayData.PaymentType,
  );

  const [notarizationOrCOTCost, setNotarizationOrCOTCost] = useState(
    isNewAssignment ? "" : overlayData.NotarizationOrCOTCost,
  );

  const [paymentFees, setPaymentFees] = useState(
    isNewAssignment ? "" : overlayData.PaymentFees,
  );

  const [deliveryCost, setDeliveryCost] = useState(
    isNewAssignment ? "" : overlayData.DeliveryCost,
  );

  const [linguist, setLinguist] = useState(
    isNewAssignment
      ? overlayData.Linguists?.find((l) => l.Name === "SmartCat") || {
          Name: "N/A",
        }
      : overlayData.Linguist,
  );

  const [translationStatus, setTranslationStatus] = useState(
    isNewAssignment
      ? "In-Progress"
      : completing
        ? "Completed"
        : overlayData.TranslationStatus,
  );

  const [accountCharge, setAccountCharge] = useState(
    isNewAssignment ? "" : overlayData.AccountCharge,
  );

  const [totalCost, setTotalCost] = useState(
    isNewAssignment ? "" : overlayData.TotalCost,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    isNewAssignment
      ? ""
      : (overlayData.TotalLinguistPay ?? overlayData.TotalCost ?? ""),
  );

  const [totalCostHelperText, setTotalCostHelperText] = useState("");
  const [accountChargeHelperText, setAccountChargeHelperText] = useState("");

  const [linguistPay, setLinguistPay] = useState(
    isNewAssignment ? 0 : overlayData.LinguistPay,
  );

  const [additionalCost, setAdditionalCost] = useState(
    isNewAssignment ? 0 : overlayData.AdditionalCost,
  );

  const [additionalLinguistCost, setAdditionalLinguistCost] = useState(
    isNewAssignment ? "" : overlayData.AdditionalLinguistCost,
  );

  const [rate, setRate] = useState(isNewAssignment ? 0.2 : overlayData.Rate);

  const [profit, setProfit] = useState(
    isNewAssignment ? "" : overlayData.Profit,
  );

  const [margin, setMargin] = useState(
    isNewAssignment ? "" : overlayData.Margin,
  );

  const [details, setDetails] = useState(
    isNewAssignment ? "" : overlayData.Details,
  );

  const [allowDetailsEditing, setAllowDetailsEditing] = useState(false);
  const [showLinguistPayAndOtherCosts, setShowLinguistPayAndOtherCosts] =
    useState(completing ? true : false);

  const [nameError, setNameError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [projectNumberError, setProjectNumberError] = useState(false);
  const [projectNumberExists, setProjectNumberExists] = useState(false);
  const [translationTypeError, setTranslationTypeError] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [requestorError, setRequestorError] = useState(false);
  const [invoiceToError, setInvoiceToError] = useState(false);
  const [SourceLanguageError, setSourceLanguageError] = useState(false);
  const [linguistError, setLinguistError] = useState(false);
  const [translationStatusError, setTranslationStatusError] = useState(false);
  const [accountChargeError, setAccountChargeError] = useState(false);
  const [totalCostError, setTotalCostError] = useState(false);
  const [linguistPayError, setLinguistPayError] = useState(false);
  const [additionalCostError, setAdditionalCostError] = useState(false);
  const [additionalLinguistCostError, setAdditionalLinguistCostError] =
    useState(false);
  const [rateError, setRateError] = useState(false);
  const [, setDeadlineError] = useState(false);
  const [deliveryDateError, setDeliveryDateError] = useState(false);
  const [unitCountError, setUnitCountError] = useState(false);
  const [deliveryTypeError, setDeliveryTypeError] = useState(false);
  const [rateTypeError, setRateTypeError] = useState(false);
  const [paymentTypeError, setPaymentTypeError] = useState(false);
  const [notarizationOrCOTCostError, setNotarizationOrCOTCostError] =
    useState(false);
  const [paymentFeesError, setPaymentFeesError] = useState(false);
  const [deliveryCostError, setDeliveryCostError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  const [shouldValidate, setShouldValidate] = useState(false);

  const [progress, setProgress] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [shakingFields, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);
  const [
    disableAutomaticCalculationOfCharges,
    setDisableAutomaticCalculationOfCharges,
  ] = useState(
    isNewAssignment ? false : overlayData.DisableAutoChargeCalc || false,
  );

  const [createServicePerLanguage, setCreateServicePerLanguage] =
    useState(false);

  const initialDataRef = useRef(null);
  const addNewTranslationRef = useRef(null);
  const updateTranslationRef = useRef(null);
  const updateCurrentProgressRef = useRef(null);
  const saveTranslationRef = useRef(null);
  const attemptSaveRef = useRef(null);

  /**
   * Helper function to get shake animation styles
   */
  const getShakeSx = (fieldId) => {
    const isShaking = shakingFields.has(fieldId);
    return isShaking
      ? {
          animation: "shake 0.6s",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
          },
        }
      : {};
  };

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const requiredFields = [
      { fieldId: "Date", value: date },
      { fieldId: "Deadline", value: deadline },
      { fieldId: "ProjectNumber", value: projectNumber },
      { fieldId: "TranslationType", value: translationType },
      { fieldId: "SourceLanguage", value: sourceLanguage },
      { fieldId: "Client", value: client },
      { fieldId: "Requestor", value: requestor },
      { fieldId: "Name", value: name },
      { fieldId: "UnitType", value: unitTypeId },
      { fieldId: "UnitCount", value: unitCount },
      { fieldId: "Rate", value: rate },
      { fieldId: "Linguist", value: linguist },
      { fieldId: "TargetLanguages", value: targetLanguages },
      { fieldId: "TranslationStatus", value: translationStatus },
      { fieldId: "AccountCharge", value: accountCharge },
    ];

    const missingRequiredFields = requiredFields.filter(
      (field) =>
        field.value === null ||
        field.value === "" ||
        (Array.isArray(field.value) && field.value.length === 0),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false;
    }

    return true;
  };

  const updateCurrentProgress = () => {
    let progress = 0;
    const total = 18;

    if (date !== null) progress++;
    if (deadline !== null) progress++;
    if (projectNumber !== null && projectNumber !== "") progress++;
    if (translationType !== null && translationType !== "") progress++;
    if (sourceLanguage !== null && sourceLanguage !== "") progress++;
    if (client !== null && client !== "") progress++;
    if (requestor !== null && requestor !== "") progress++;
    if (name !== null && name !== "") progress++;
    if (unitTypeId !== null && unitTypeId !== "") progress++;
    if (unitCount !== null && unitCount !== "") progress++;
    if (rate !== null && rate !== "") progress++;
    if (linguist !== null && linguist !== "") progress++;
    if (targetLanguages !== null && targetLanguages.length > 0) progress++;
    if (translationStatus !== null && translationStatus !== "") progress++;
    if (accountCharge !== null && accountCharge !== "") progress++;
    if (details !== null && details !== "") progress++;
    if ((totalCost !== null && totalCost !== "") || !completing) progress++;
    if ((linguistPay !== null && linguistPay !== "") || !completing) progress++;

    const percentProgress = (progress * 100) / total;
    const message =
      progress === total
        ? "All Done!"
        : `${progress}/${total} fields completed`;
    setProgressMessage(Number(percentProgress).toFixed(2) + "%: " + message);
    setProgress(percentProgress);
  };

  const fillDetails = (
    requestor,
    name,
    unitCount,
    sourceLanguage,
    targetLanguages,
    translationType,
  ) => {
    // Ensure values are truthy; if empty string or null, treat them as missing.
    const req = requestor ? requestor.trim() : "";
    const nm = name ? name.trim() : "";
    const lang = sourceLanguage ? sourceLanguage.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasSourceLanguage = lang.length > 0;
    const hasSelectedLanguage =
      Array.isArray(targetLanguages) && targetLanguages.length > 0;

    const isMinimum =
      unitTypeName === "words" &&
      typeof unitCount === "number" &&
      unitCount > 0 &&
      unitCount < 245;

    // Build the details parts dynamically
    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const unitLabel =
        Number(unitCount) === 1 ? unitTypeName : `${unitTypeName}s`;

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    if (hasSourceLanguage && hasSelectedLanguage) {
      let languagePart = lang + " to ";

      const selectedLangNames = targetLanguages.reduce((acc, item, index) => {
        if (index === targetLanguages.length - 2) {
          // Add ', and ' if it's the item immediately before last
          return acc + item.Name + ", and ";
        } else if (index < targetLanguages.length - 1) {
          // For other items that are not the last, add a comma and a space
          return acc + item.Name + ", ";
        } else {
          // For the last item, append the name without any punctuation
          return acc + item.Name;
        }
      }, "");

      languagePart += selectedLangNames + " " + translationType;

      detailsParts.push(languagePart);
    }

    if (isMinimum) {
      detailsParts.push("Minimum");
    }

    // If there are valid parts, join them with " - " and append " Translation", otherwise set an empty string.
    if (detailsParts.length > 0) {
      setDetails(detailsParts.join(" - "));
    } else {
      setDetails("");
    }
  };

  const applyTargetLanguages = (
    nextTargetLanguages,
    selectedSourceLanguage = sourceLanguage,
  ) => {
    const normalizedTargetLanguages = normalizeTargetLanguages(
      nextTargetLanguages,
      selectedSourceLanguage,
    );

    fillDetails(
      requestor.Name,
      name,
      Number(unitCount),
      selectedSourceLanguage?.Name || "",
      normalizedTargetLanguages,
      translationType,
    );

    setNumberOfLanguages(normalizedTargetLanguages.length);
    setTargetLanguages(normalizedTargetLanguages);
  };

  // Helper function to generate details string for a single language (for per-language services)
  const generateDetailsForLanguage = (
    requestorName,
    serviceName,
    unitCount,
    sourceLanguageName,
    targetLanguageName,
    translationType,
  ) => {
    const req = requestorName ? requestorName.trim() : "";
    const nm = serviceName ? serviceName.trim() : "";
    const lang = sourceLanguageName ? sourceLanguageName.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasUnitCount = typeof unitCount === "number" && unitCount > 0;
    const hasSourceLanguage = lang.length > 0;
    const hasTargetLanguage =
      targetLanguageName && targetLanguageName.trim().length > 0;

    const isMinimum =
      unitTypeName === "words" &&
      typeof unitCount === "number" &&
      unitCount > 0 &&
      unitCount < 245;

    const detailsParts = [];

    if (hasRequestor) {
      detailsParts.push(req);
    }

    if (hasName) {
      detailsParts.push(nm);
    }

    if (hasUnitCount) {
      const unitLabel =
        Number(unitCount) === 1 ? unitTypeName : `${unitTypeName}s`;

      detailsParts.push(`${Number(unitCount).toLocaleString()} ${unitLabel}`);
    }

    if (hasSourceLanguage && hasTargetLanguage) {
      const languagePart = `${lang} to ${targetLanguageName} ${translationType}`;
      detailsParts.push(languagePart);
    }

    if (isMinimum) {
      detailsParts.push("Minimum");
    }

    return detailsParts.length > 0 ? detailsParts.join(" - ") : "";
  };

  dayjs.extend(utc);
  dayjs.extend(durationPlugin);

  // Auto-calculate charges based on unit count, rate, and other factors
  useEffect(() => {
    // If automatic calculation is disabled, skip it
    if (disableAutomaticCalculationOfCharges) {
      return;
    }

    // If required fields are missing, don't calculate
    if (!unitCount || !rate || numberOfLanguages === null) {
      return;
    }

    // Use 1 for calculations when creating service per language
    const effectiveNumberOfLanguages =
      createServicePerLanguage && targetLanguages.length > 1
        ? 1
        : numberOfLanguages;

    let charge =
      parseFloat(unitCount) *
        parseFloat(effectiveNumberOfLanguages) *
        parseFloat(rate) +
      parseFloat(notarizationOrCOTCost || 0) +
      parseFloat(additionalLinguistCost || 0) +
      parseFloat(additionalCost || 0) +
      parseFloat(deliveryCost || 0) +
      parseFloat(paymentFees || 0);

    const chargeText =
      "( " +
      parseFloat(unitCount) +
      " x " +
      parseFloat(effectiveNumberOfLanguages) +
      " x " +
      parseFloat(rate) +
      " ) + " +
      parseFloat(notarizationOrCOTCost || 0) +
      " + " +
      parseFloat(additionalLinguistCost || 0) +
      " + " +
      parseFloat(additionalCost || 0) +
      " + " +
      parseFloat(deliveryCost || 0) +
      " + " +
      parseFloat(paymentFees || 0);

    const totalLinguistPayValue =
      parseFloat(linguistPay || 0) +
      parseFloat(notarizationOrCOTCost || 0) +
      parseFloat(additionalLinguistCost || 0) +
      parseFloat(deliveryCost || 0);

    const totCost =
      totalLinguistPayValue +
      parseFloat(additionalCost || 0) +
      parseFloat(paymentFees || 0);

    const totCostText =
      parseFloat(linguistPay || 0) +
      " + " +
      parseFloat(notarizationOrCOTCost || 0) +
      " + " +
      parseFloat(additionalLinguistCost || 0) +
      " + " +
      parseFloat(deliveryCost || 0) +
      " + " +
      parseFloat(additionalCost || 0) +
      " + " +
      parseFloat(paymentFees || 0);

    // Apply $49 minimum for automatic calculations
    charge = charge >= 49 ? charge : 49;
    setAccountCharge(Number(charge).toFixed(2));
    setAccountChargeHelperText(chargeText);

    setTotalCostHelperText(totCostText);
    setTotalLinguistPay(Number(totalLinguistPayValue).toFixed(2));
    setTotalCost(Number(totCost).toFixed(2));

    const prof = parseFloat(charge) - parseFloat(totCost);
    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [
    unitCount,
    numberOfLanguages,
    rate,
    notarizationOrCOTCost,
    additionalLinguistCost,
    additionalCost,
    deliveryCost,
    paymentFees,
    linguistPay,
    disableAutomaticCalculationOfCharges,
    createServicePerLanguage,
    targetLanguages,
  ]);

  useEffect(() => {
    if (totalCost === "") {
      setTotalLinguistPay("");
      return;
    }

    const derivedTotalLinguistPay =
      parseFloat(totalCost || 0) -
      parseFloat(paymentFees || 0) -
      parseFloat(additionalCost || 0);

    setTotalLinguistPay(
      isNaN(derivedTotalLinguistPay) || !isFinite(derivedTotalLinguistPay)
        ? ""
        : Number(derivedTotalLinguistPay).toFixed(2),
    );
  }, [totalCost, paymentFees, additionalCost]);

  // Recalculate profit and margin whenever accountCharge or totalCost changes
  useEffect(() => {
    const charge = parseFloat(accountCharge || 0);
    const totCost = parseFloat(totalCost || 0);
    const prof = parseFloat(charge) - parseFloat(totCost);

    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const marginValue = (prof / charge) * 100;
      setMargin(
        isNaN(marginValue) || !isFinite(marginValue)
          ? 0
          : marginValue.toFixed(2),
      );
    }
  }, [accountCharge, totalCost]);

  const validateFields = () => {
    setNameError(name === null);
    setDateError(date === null);
    setProjectNumberError(projectNumber === null);
    setTranslationTypeError(translationType === null);
    setClientError(client === null);
    setRequestorError(requestor === null);
    setSourceLanguageError(sourceLanguage === null);
    setLinguistError(linguist === null);
    setRateError(rate === null);
    setTranslationStatusError(translationStatus === null);
    setAccountChargeError(accountCharge === null);
    setTotalCostError(totalCost === null);
    setLinguistPayError(linguistPay === null);
    setDetailsError(details === null);
  };

  // Check if assignment was paid and if critical fields have changed
  const checkPaidWarning = () => {
    // Only check for existing assignments
    if (isNewAssignment) return false;

    // Check if the assignment was paid
    const wasPaid = overlayData.Paid === true || overlayData.Paid === 1;

    if (!wasPaid) return false;

    // Check if critical fields have changed
    const criticalChanges = [];

    // Check if client changed
    if (client?.id !== overlayData.Client?.id) {
      criticalChanges.push("Client");
    }

    // Check if linguist changed
    if (linguist?.id !== overlayData.Linguist?.id) {
      criticalChanges.push("Linguist");
    }

    // Check if account charge changed
    if (Number(accountCharge) !== Number(overlayData.AccountCharge)) {
      criticalChanges.push("Account Charge");
    }

    // Check if total cost changed
    if (Number(totalCost) !== Number(overlayData.TotalCost)) {
      criticalChanges.push("Total Cost");
    }

    // Check if linguist pay changed
    if (Number(linguistPay) !== Number(overlayData.LinguistPay)) {
      criticalChanges.push("Linguist Pay");
    }

    if (criticalChanges.length > 0) {
      let message = "⚠️ WARNING: This assignment has been marked as PAID.\n\n";
      message += `You are changing: ${criticalChanges.join(", ")}\n\n`;

      // Special check for linguist change - check if the linguist was paid
      if (criticalChanges.includes("Linguist")) {
        message +=
          "🔹 You are changing the linguist for this paid assignment.\n";
        message += `   Previous linguist: ${overlayData.Linguist?.Name || "None"}\n`;
        message += `   New linguist: ${linguist?.Name || "None"}\n\n`;
      }

      message += "Are you sure you want to proceed with these changes?";

      setPaidWarningMessage(message);
      return true;
    }

    return false;
  };

  function addNewTranslation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const normalizedUnitTypeId = unitTypeId === "" ? null : Number(unitTypeId);
    const normalizedUnitCount = unitCount === "" ? null : Number(unitCount);
    const legacyWordCount =
      unitTypeName === "words" ? normalizedUnitCount : null;

    // Check if we should create one service per language
    if (createServicePerLanguage && targetLanguages.length > 1) {
      // Create one service for each target language
      const servicePromises = targetLanguages.map((targetLanguage, index) => {
        const currentProjectNumber = String(Number(projectNumber) + index);

        // Generate details specific to this language
        const languageSpecificDetails = generateDetailsForLanguage(
          requestor?.Name || "",
          name,
          normalizedUnitCount,
          sourceLanguage?.Name || "",
          targetLanguage.Name,
          translationType,
        );

        const data = {
          Assignment: {
            id: 0,
            Date: date.format("YYYY-MM-DD"),
            Deadline:
              deadline && deadline.isValid()
                ? deadline.format("YYYY-MM-DD")
                : null,
            DeliveryDate:
              deliveryDate && deliveryDate.isValid()
                ? deliveryDate.format("YYYY-MM-DD")
                : null,
            ProjectNumber: currentProjectNumber,
            CheckingDate: null,
            ServiceCategory: "Translation",
            Name: name,
            Details: languageSpecificDetails,
            TranslationStatus:
              translationStatus === "" ? null : translationStatus,
            AccountCharge: accountCharge === "" ? null : accountCharge,
            TotalCost: totalCost === "" ? null : totalCost,
            TotalLinguistPay:
              totalLinguistPay === ""
                ? null
                : Number(totalLinguistPay).toFixed(2),
            AdditionalLinguistCost:
              additionalLinguistCost === "" ? null : additionalLinguistCost,
            AdditionalCost: additionalCost === "" ? null : additionalCost,
            LinguistPay: linguistPay === "" ? null : linguistPay,
            NotarizationOrCOTCost:
              notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
            DeliveryCost: deliveryCost === "" ? null : deliveryCost,
            DeliveryType: deliveryType === "" ? null : deliveryType,
            PaymentFees: paymentFees === "" ? null : paymentFees,
            PaymentType: paymentType === "" ? null : paymentType,
            Profit: profit === "" ? null : profit,
            Margin: margin === "" ? null : margin,
            TranslationType: translationType,
            Paid: false,
            RateType: rateType === "" ? null : rateType,
            Rate: rate === "" ? null : rate,
            UnitTypeID: normalizedUnitTypeId,
            UnitCount: normalizedUnitCount,
            WordCount: legacyWordCount,
            Deleted: false,
            Cancelled: false,
            NumberOfLanguages: 1, // Single language per service
            DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
            Invoiced: false,
            CancellationReason: null,
            CreatedBy: currentUser.id,
            Checker: null,
          },
          SourceLanguage: sourceLanguage,
          TargetLanguages: [targetLanguage], // Only this language
          Client: client,
          Requestor: requestor,
          Linguist: linguist,
          Invoicer: invoiceTo,
        };

        return api.post("/services/translations/addNewTranslation", {
          data: data,
        });
      });

      // Wait for all services to be created
      Promise.all(servicePromises)
        .then((responses) => {
          // Mark save complete and refresh UI immediately
          setIsSavingChanges(false);
          triggerPageRefresh(
            `${responses.length} Translations Added Successfully!`,
            theme,
          );

          // Emit assignment saved event for other viewers for each service
          if (socket) {
            responses.forEach((response) => {
              const projectNumberSafe = response.data.ProjectNumber || "";
              socket.emit("assignment:saved", {
                modelType: "Translation",
                recordId: response.data.ServiceID || response.data.id,
                projectNumber: projectNumberSafe,
                user: {
                  id: currentUser.id,
                  name: currentUser.Name,
                  imageThumbnail: currentUser.ImageThumbnail,
                },
              });
            });

            socket.emit("assignment:saved", {
              modelType: "TranslationsList",
              recordId: "all",
              projectNumber: projectNumber,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
            });
          }

          releaseLock();
          setOverlayName("");
        })
        .catch((err) => {
          console.log(err);
          setIsSavingChanges(false);
          setIsEditable(true);
        });

      return;
    }

    // Original behavior: create one service with all languages
    const data = {
      Assignment: {
        id: 0,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: name,
        Details: details,
        TranslationStatus: translationStatus === "" ? null : translationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        TotalLinguistPay:
          totalLinguistPay === "" ? null : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        NotarizationOrCOTCost:
          notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        PaymentType: paymentType === "" ? null : paymentType,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        TranslationType: translationType,
        Paid: false,
        RateType: rateType === "" ? null : rateType,
        Rate: rate === "" ? null : rate,
        UnitTypeID: normalizedUnitTypeId,
        UnitCount: normalizedUnitCount,
        WordCount: legacyWordCount,
        Deleted: false,
        Cancelled: false,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      SourceLanguage: sourceLanguage,
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
    };
    api
      .post("/services/translations/addNewTranslation", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          Deadline_FROM: null,
          Deadline_TO: response.data.Deadline,
          DeliveryDate_FROM: null,
          DeliveryDate_TO: response.data.DeliveryDate,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          TranslationStatus_FROM: null,
          TranslationStatus_TO: response.data.TranslationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,

          NotarizationOrCOTCost_FROM: null,
          NotarizationOrCOTCost_TO: response.data.NotarizationOrCOTCost,

          PaymentFees_FROM: null,
          PaymentFees_TO: response.data.PaymentFees,

          DeliveryCost_FROM: null,
          DeliveryCost_TO: response.data.DeliveryCost,

          PaymentType_FROM: null,
          PaymentType_TO: response.data.PaymentType,

          DeliveryType_FROM: null,
          DeliveryType_TO: response.data.DeliveryType,

          NumberOfLanguages_FROM: null,
          NumberOfLanguages_TO: response.data.NumberOfLanguages,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          TranslationType_FROM: null,
          TranslationType_TO: response.data.TranslationType,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateType_FROM: null,
          RateType_TO: response.data.RateType,
          UnitTypeID_FROM: null,
          UnitTypeID_TO: response.data.UnitTypeID,
          UnitCount_FROM: null,
          UnitCount_TO: response.data.UnitCount,
          WordCount_FROM: null,
          WordCount_TO: response.data.WordCount,

          SourceLanguage_FROM: null,
          SourceLanguage_TO: sourceLanguage.id,

          DisableAutoChargeCalc_FROM: null,
          DisableAutoChargeCalc_TO: response.data.DisableAutoChargeCalc,

          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: targetLanguages,

          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };

        void dataHistory;

        // Mark save complete and refresh UI immediately
        setIsSavingChanges(false);
        triggerPageRefresh("New Translation Added Successfully!", theme);

        // Emit assignment saved event for other viewers
        if (socket) {
          const projectNumberSafe =
            projectNumber || response.data.ProjectNumber || "";
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: response.data.ServiceID || response.data.id,
            projectNumber: projectNumberSafe,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            projectNumber: projectNumberSafe,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        releaseLock();
        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  function updateTranslation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const normalizedUnitTypeId = unitTypeId === "" ? null : Number(unitTypeId);
    const normalizedUnitCount = unitCount === "" ? null : Number(unitCount);
    const legacyWordCount =
      unitTypeName === "words" ? normalizedUnitCount : null;

    const dataOld = {
      Assignment: {
        id: overlayData.id,
        Date: overlayData.Date,
        Deadline: overlayData.Deadline,
        ProjectNumber: overlayData.ProjectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: overlayData.Name,
        Details: overlayData.Details,
        TranslationStatus:
          overlayData.TranslationStatus === ""
            ? null
            : overlayData.TranslationStatus,
        AccountCharge:
          overlayData.AccountCharge === "" ? null : overlayData.AccountCharge,
        TotalCost: overlayData.TotalCost === "" ? null : overlayData.TotalCost,
        TotalLinguistPay:
          overlayData.TotalLinguistPay === "" ||
          overlayData.TotalLinguistPay == null
            ? null
            : Number(overlayData.TotalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          overlayData.AdditionalLinguistCost === "" ||
          overlayData.AdditionalLinguistCost == null
            ? null
            : Number(overlayData.AdditionalLinguistCost).toFixed(2),
        AdditionalCost:
          overlayData.AdditionalCost === "" ||
          overlayData.AdditionalCost === null
            ? "0.00"
            : overlayData.AdditionalCost,
        LinguistPay:
          overlayData.LinguistPay === "" ? null : overlayData.LinguistPay,
        NotarizationOrCOTCost:
          overlayData.NotarizationOrCOTCost === ""
            ? null
            : overlayData.NotarizationOrCOTCost,
        DeliveryCost:
          overlayData.DeliveryCost === "" ? null : overlayData.DeliveryCost,
        DeliveryType:
          overlayData.DeliveryType === "" ? null : overlayData.DeliveryType,
        DeliveryDate: overlayData.DeliveryDate,
        PaymentFees:
          overlayData.PaymentFees === "" ? null : overlayData.PaymentFees,
        PaymentType:
          overlayData.PaymentType === "" ? null : overlayData.PaymentType,
        Profit: overlayData.Profit === "" ? null : overlayData.Profit,
        Margin: overlayData.Margin === "" ? null : overlayData.Margin,
        TranslationType:
          overlayData.TranslationType === ""
            ? null
            : overlayData.TranslationType,
        Paid: false,
        RateType: overlayData.RateType === "" ? null : overlayData.RateType,
        Rate: overlayData.Rate === "" ? null : overlayData.Rate,
        UnitTypeID:
          overlayData.UnitTypeID === ""
            ? null
            : overlayData.UnitTypeID || overlayData.UnitType?.id,
        UnitCount: overlayData.UnitCount === "" ? null : overlayData.UnitCount,
        WordCount: overlayData.WordCount === "" ? null : overlayData.WordCount,
        NumberOfLanguages:
          overlayData.NumberOfLanguages === ""
            ? null
            : overlayData.NumberOfLanguages,
        DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      SourceLanguage: overlayData.Language,
      TargetLanguages: overlayData.ServiceTargetLanguages,
      Client: overlayData.Client,
      Requestor: overlayData.Requestor,
      Linguist: overlayData.Linguist,
      Invoicer: overlayData.Invoicer,
    };

    const dataNew = {
      Assignment: {
        id: overlayData.id,
        Date: date.format("YYYY-MM-DD"),
        Deadline:
          deadline && deadline.isValid() ? deadline.format("YYYY-MM-DD") : null,
        ProjectNumber: projectNumber,
        //date.format("YYYY-MM-DD") === overlayData.Date
        //  ? overlayData.ProjectNumber
        //  : projectNumber,
        CheckingDate: null,
        ServiceCategory: "Translation",
        Name: name,
        Details: details,
        TranslationStatus: translationStatus === "" ? null : translationStatus,
        AccountCharge:
          accountCharge === "" ? null : Number(accountCharge).toFixed(2),
        TotalCost: totalCost === "" ? null : Number(totalCost).toFixed(2),
        TotalLinguistPay:
          totalLinguistPay === "" ? null : Number(totalLinguistPay).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === ""
            ? null
            : Number(additionalLinguistCost).toFixed(2),
        AdditionalCost:
          additionalCost === "" ? null : Number(additionalCost).toFixed(2),
        LinguistPay: linguistPay === "" ? null : Number(linguistPay).toFixed(2),
        NotarizationOrCOTCost:
          notarizationOrCOTCost === "" ? null : notarizationOrCOTCost,
        DeliveryCost: deliveryCost === "" ? null : deliveryCost,
        DeliveryDate:
          deliveryDate && deliveryDate.isValid()
            ? deliveryDate.format("YYYY-MM-DD")
            : null,
        PaymentFees: paymentFees === "" ? null : paymentFees,
        PaymentType: paymentType === "" ? null : paymentType,
        DeliveryType: deliveryType === "" ? null : deliveryType,
        Profit: profit === "" ? null : Number(profit).toFixed(2),
        Margin: margin === "" ? null : Number(margin).toFixed(2),
        TranslationType: translationType,
        Paid: false,
        RateType: rateType === "" ? null : rateType,
        Rate: rate === "" ? null : Number(rate).toFixed(2),
        UnitTypeID: normalizedUnitTypeId,
        UnitCount: normalizedUnitCount,
        WordCount: legacyWordCount,
        NumberOfLanguages: targetLanguages.length,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        Deleted: false,
        Cancelled: false,
        Invoiced: false,
        CancellationReason: null,
        CreatedBy: currentUser.id,
        Checker: null,
      },
      SourceLanguage: sourceLanguage,
      TargetLanguages: targetLanguages,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/translations/updateTranslation", {
        data: allData,
      })
      .then((response) => {
        const historyData = {
          ...response.data,
          Status: "Updated",
          ChangedBy: currentUser.id,
          DisableAutoChargeCalc_FROM:
            overlayData.DisableAutoChargeCalc || false,
          DisableAutoChargeCalc_TO: disableAutomaticCalculationOfCharges,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };

        void historyData;

        // Mark save complete and refresh UI immediately
        setIsSavingChanges(false);
        triggerPageRefresh("Translation Assignment Updated Sucessfully", theme);

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: overlayData.id,
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            projectNumber: projectNumber,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        releaseLock();
        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          dateError ||
          projectNumberError ||
          nameError ||
          translationTypeError ||
          clientError ||
          requestorError ||
          SourceLanguageError ||
          linguistError ||
          rateError ||
          translationStatusError ||
          accountChargeError ||
          totalCostError ||
          linguistPayError ||
          detailsError
        )
      ) {
        isNewAssignment
          ? addNewTranslationRef.current()
          : updateTranslationRef.current();
      }
      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    nameError,
    dateError,
    projectNumberError,
    translationTypeError,
    clientError,
    requestorError,
    SourceLanguageError,
    linguistError,
    rateError,
    translationStatusError,
    accountChargeError,
    totalCostError,
    linguistPayError,
    detailsError,
    isNewAssignment,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    name,
    date,
    deadline,
    projectNumber,
    translationType,
    client,
    requestor,
    sourceLanguage,
    linguist,
    rate,
    rateType,
    unitTypeId,
    unitCount,
    translationStatus,
    targetLanguages,
    accountCharge,
    totalCost,
    linguistPay,
    details,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        name: overlayData.Name,
        date: overlayData.Date, // assuming string format "YYYY-MM-DD"
        deadline: overlayData.Deadline,
        projectNumber: overlayData.ProjectNumber,
        translationType: overlayData.TranslationType,
        client: overlayData.Client, // if client is an object, consider comparing by id
        requestor: overlayData.Requestor, // comparing by Name (or id if available)
        invoiceTo: overlayData.Invoicer,
        targetLanguages: overlayData.ServiceTargetLanguages,
        sourceLanguage: overlayData.Language,
        linguist: overlayData.Linguist,
        translationStatus: overlayData.TranslationStatus,
        accountCharge: overlayData.AccountCharge,
        totalCost: overlayData.TotalCost,
        linguistPay: overlayData.LinguistPay,
        additionalLinguistCost: overlayData.AdditionalLinguistCost,
        additionalCost: overlayData.AdditionalCost,
        unitTypeId: overlayData.UnitTypeID,
        unitCount: overlayData.UnitCount ?? overlayData.WordCount,
        rate: overlayData.Rate,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const languageList = (initialDataRef.current.targetLanguages || []).map(
        (targetLanguage) => targetLanguage.Language,
      );

      const dirty =
        name !== initialDataRef.current.name ||
        (date && date.format("YYYY-MM-DD")) !== initialDataRef.current.date ||
        (deadline && deadline.format("YYYY-MM-DD")) !==
          initialDataRef.current.deadline ||
        Number(projectNumber) !==
          Number(initialDataRef.current.projectNumber) ||
        translationType !== initialDataRef.current.translationType ||
        client?.Account !== initialDataRef.current.client?.Account ||
        requestor?.Name !== initialDataRef.current.requestor?.Name ||
        invoiceTo?.Name !== initialDataRef.current.invoiceTo?.Name ||
        JSON.stringify(targetLanguages) !== JSON.stringify(languageList) ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        sourceLanguage?.Name !== initialDataRef.current.sourceLanguage?.Name ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        translationStatus !== initialDataRef.current.translationStatus || //Error here
        Number(accountCharge) !==
          Number(initialDataRef.current.accountCharge) ||
        Number(totalCost) !== Number(initialDataRef.current.totalCost) ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        Number(additionalLinguistCost) !==
          Number(initialDataRef.current.additionalLinguistCost) ||
        Number(additionalCost) !==
          Number(initialDataRef.current.additionalCost) ||
        Number(unitTypeId || 0) !==
          Number(initialDataRef.current.unitTypeId || 0) ||
        (Number(unitCount) || 0) !==
          (Number(initialDataRef.current.unitCount) || 0) ||
        Number(rate) !== Number(initialDataRef.current.rate);
      setHasChanges(dirty);
    }
  }, [
    name,
    date,
    deadline,
    projectNumber,
    translationType,
    client,
    requestor,
    invoiceTo,
    targetLanguages,
    sourceLanguage,
    linguist,
    translationStatus,
    accountCharge,
    totalCost,
    linguistPay,
    additionalLinguistCost,
    additionalCost,
    unitTypeId,
    unitCount,
    rate,
    isNewAssignment,
  ]);

  const saveTranslation = () => {
    // Check if we need to show paid warning
    if (!pendingSave && checkPaidWarning()) {
      setShowPaidWarning(true);
      return;
    }

    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);
    validateFields();
    setShouldValidate(true);
  };

  const attemptSave = () => {
    if (isSavingChanges || isLocked) {
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    if (isNewAssignment && projectNumberExists) {
      openDialog({
        theme,
        title: "Duplicate Project Number",
        message: `The project number ${projectNumber} already exists. Create another assignment with the same number?`,
        primaryAction: "Create Anyway",
        secondaryAction: "Cancel",
        primaryOnClick: () => {
          closeDialog();
          saveTranslation();
        },
        secondaryOnClick: closeDialog,
      });
      return;
    }

    saveTranslation();
  };

  // Handle confirmation of paid assignment warning
  const handlePaidWarningConfirm = () => {
    setShowPaidWarning(false);
    setPendingSave(true);
    // Trigger save again, but this time with pendingSave = true to bypass the check
    setTimeout(() => saveTranslation(), 0);
  };

  // Handle cancellation of paid assignment warning
  const handlePaidWarningCancel = () => {
    setShowPaidWarning(false);
    setPendingSave(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSaveRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  addNewTranslationRef.current = addNewTranslation;
  updateTranslationRef.current = updateTranslation;
  updateCurrentProgressRef.current = updateCurrentProgress;
  saveTranslationRef.current = saveTranslation;
  attemptSaveRef.current = attemptSave;

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          height: { xs: "95%", sm: "75%" },
          maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "100%",
              flexDirection: "row",
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => {
                releaseLock();
                setOverlayName("");
              }}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {isNewAssignment
                ? "New Translation Assignment"
                : completing
                  ? "Complete Translation Assignment"
                  : "Edit Translation Assignment"}
            </Typography>
          </Box>
        </Box>

        <Box
          component="form"
          sx={{
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            border: `1px solid ${theme.palette.background.default}`,
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.default,
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
              height: "90%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <MobileDatePicker
                  disabled={!isEditable}
                  id={"Date"}
                  label="Date"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={date}
                  onChange={(newValue) => {
                    setDate(newValue);
                    setDateError(newValue === null);
                  }}
                  sx={{
                    ...getShakeSx("Date"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (date && date.format("YYYY-MM-DD")) || "",
                          overlayData?.Date || "",
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: dateError,
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <MobileDatePicker
                  disabled={!isEditable}
                  id={"Deadline"}
                  label="Deadline"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={deadline}
                  onChange={(newValue) => {
                    setDeadline(newValue);
                    setDeadlineError(newValue === null);
                  }}
                  sx={{
                    ...getShakeSx("Deadline"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (deadline && deadline.format("YYYY-MM-DD")) || "",
                          overlayData.Deadline,
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: dateError,
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"ProjectNumber"}
                  label="Project Number"
                  error={projectNumberError || projectNumberExists}
                  value={projectNumber}
                  onChange={(event) => {
                    setProjectNumber(event.target.value);
                    setProjectNumberError(event.target.value === null);
                    setProjectNumberExists(false);
                  }}
                  onBlur={async () => {
                    if (projectNumber && isNewAssignment) {
                      try {
                        const response = await api.get(
                          `/services/check-project-number?projectNumber=${projectNumber}`,
                        );
                        setProjectNumberExists(response.data.exists);
                      } catch (error) {
                        console.error("Error checking project number:", error);
                      }
                    }
                  }}
                  helperText={
                    projectNumberExists
                      ? "This project number already exists (Ignore if duplicate)."
                      : ""
                  }
                  sx={{
                    ...getShakeSx("ProjectNumber"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: projectNumberExists
                          ? theme.palette.warning.main
                          : getBorderColor(
                              theme,
                              isNewAssignment,
                              Number(projectNumber) || "",
                              Number(overlayData.ProjectNumber),
                            ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"TranslationType"}
                  label="Translation Type"
                  error={translationTypeError}
                  value={translationType}
                  onChange={(event) => {
                    setTranslationType(event.target.value);
                    setTranslationTypeError(event.target.value === null);

                    fillDetails(
                      requestor.Name,
                      name,
                      Number(unitCount),
                      sourceLanguage.Name,
                      targetLanguages,
                      event.target.value,
                    );
                  }}
                  sx={{
                    ...getShakeSx("TranslationType"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          translationType || "",
                          overlayData.TranslationType,
                        ),
                      },
                    },
                  }}
                >
                  {overlayData.TranslationTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Name"}
                label="Name"
                error={nameError}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(event.target.value === null);

                  fillDetails(
                    requestor.Name,
                    event.target.value,
                    Number(unitCount),
                    sourceLanguage.Name,
                    targetLanguages,
                    translationType,
                  );
                }}
                sx={{
                  ...getShakeSx("Name"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,
                        name || "",
                        overlayData.Name,
                      ),
                    },
                  },
                }}
              />
            </Grid>

            {completing ? null : null}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disablePortal
                  disableClearable
                  key={"Language"}
                  size="small"
                  options={overlayData.Languages}
                  getOptionLabel={(language) => language?.Name || ""}
                  value={sourceLanguage}
                  onChange={(event, newValue) => {
                    let selectedSourceLanguage;

                    if (typeof newValue === "string") {
                      selectedSourceLanguage = {
                        Name: newValue,
                      };
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      selectedSourceLanguage = {
                        Name: newValue.inputValue,
                      };
                    } else {
                      selectedSourceLanguage = newValue;
                    }

                    setSourceLanguage(selectedSourceLanguage);
                    applyTargetLanguages(
                      targetLanguages,
                      selectedSourceLanguage,
                    );

                    setSourceLanguageError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Language"
                      placeholder=""
                      error={SourceLanguageError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("SourceLanguage"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (sourceLanguage && sourceLanguage.Name) || "",
                          (overlayData &&
                            overlayData.Language &&
                            overlayData.Language.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disablePortal
                  disableClearable
                  key={"Client"}
                  size="small"
                  options={overlayData.Clients}
                  getOptionLabel={(client) => client?.Account || ""}
                  value={client}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setClient({
                        Account: newValue,
                      });
                      setAddNewClient(false);
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setClient({
                        Account: newValue.inputValue,
                      });

                      setAddNewClient(true);
                    } else {
                      setClient(newValue);
                      setAddNewClient(false);
                    }

                    setRequestor("");

                    setClientError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const { inputValue } = params;

                    // Filter by both Account and ShortName
                    const filtered = options.filter((option) => {
                      const accountMatch =
                        option.Account?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      const shortNameMatch =
                        option.ShortName?.toLowerCase().includes(
                          inputValue.toLowerCase(),
                        );
                      return accountMatch || shortNameMatch;
                    });

                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) =>
                        inputValue === option.Account ||
                        inputValue === option.ShortName,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Account: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client"
                      placeholder=""
                      error={clientError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Client"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (client && client.Account) || "",
                          (overlayData.Client && overlayData.Client.Account) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disablePortal
                  disableClearable
                  key={"Requestor"}
                  size="small"
                  id={"Requestor"}
                  options={(client && client.Requestors) || []}
                  getOptionLabel={(requestor) => requestor?.Name || ""}
                  value={requestor}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setRequestor({
                        Name: newValue,
                      });
                      setAddNewRequestor(false);
                      fillDetails(
                        newValue,
                        name,
                        Number(unitCount),
                        sourceLanguage.Name,
                        targetLanguages,
                        translationType,
                      );
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setRequestor({
                        Name: newValue.inputValue,
                      });
                      setAddNewRequestor(true);
                      fillDetails(
                        newValue.inputValue,
                        name,
                        Number(unitCount),
                        sourceLanguage.Name,
                        targetLanguages,
                        translationType,
                      );
                    } else {
                      setRequestor(newValue);
                      setAddNewRequestor(false);
                      fillDetails(
                        newValue.Name,
                        name,
                        Number(unitCount),
                        sourceLanguage.Name,
                        targetLanguages,
                        translationType,
                      );
                    }

                    setRequestorError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Requestor"
                      placeholder=""
                      error={requestorError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Requestor"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (requestor && requestor.Name) || "",
                          (overlayData &&
                            overlayData.Requestor &&
                            overlayData.Requestor.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}
            {completing ? null : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disablePortal
                  disableClearable
                  key={"Linguist"}
                  size="small"
                  options={overlayData.Linguists}
                  getOptionLabel={(linguist) => linguist?.Name || ""}
                  value={linguist}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setLinguist({
                        Name: newValue,
                      });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setLinguist({
                        Name: newValue.inputValue,
                      });
                    } else {
                      setLinguist(newValue);
                    }

                    setLinguistError(newValue === null);
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linguist"
                      placeholder=""
                      error={linguistError}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Linguist"),
                    "& .MuiOutlinedInput-root": {
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          linguist.Name || "",
                          (overlayData.Linguist && overlayData.Linguist.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                disabled={!isEditable}
                disablePortal
                disableClearable
                key={"InvoiceTo"}
                size="small"
                id={"InvoiceTo"}
                options={(client && client.Requestors) || []}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                value={invoiceTo}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setInvoiceTo({
                      Name: newValue,
                    });
                    setAddNewInvoiceTo(false);
                  } else if (newValue && newValue.inputValue) {
                    // Create a new value from the user input
                    setInvoiceTo({
                      Name: newValue.inputValue,
                    });
                    setAddNewInvoiceTo(true);
                  } else {
                    setInvoiceTo(newValue);
                    setAddNewInvoiceTo(false);
                  }

                  setInvoiceToError(newValue === null);
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);

                  const { inputValue } = params;
                  // Suggest the creation of a new value
                  const isExisting = options.some(
                    (option) => inputValue === option.Name,
                  );
                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      inputValue,
                      Name: `Add "${inputValue}"`,
                    });
                  }

                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice To"
                    placeholder=""
                    error={invoiceToError}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.MuiInputBase-root fieldset": {
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        (invoiceTo && invoiceTo.Name) || "",
                        (overlayData.Invoicer && overlayData.Invoicer.Name) ||
                          "",
                        true,
                      ),
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id={"RateTypes"}
                label="Rate Types"
                error={rateTypeError}
                value={rateType}
                onChange={(event) => {
                  setRateType(event.target.value);
                  setRateTypeError(event.target.value === null);

                  switch (event.target.value) {
                    case "Rushed Translation":
                      setRate(0.25);
                      break;
                    case "Translation":
                      setRate(0.2);
                      break;
                    case "Custom":
                      setRate(0);
                      break;
                    case "Voiceover":
                      setRate(0);
                      break;
                    case "Design":
                      setRate(0);
                      break;
                    case "DTP":
                      setRate(0);
                      break;
                    default:
                      setRate(0);
                      break;
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        rateType || "",
                        overlayData.RateType,
                      ),
                    },
                  },
                }}
              >
                {overlayData.RateTypes.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Rate"}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {`Per ${unitTypeAbbrev}`}
                      </InputAdornment>
                    ),
                  },
                }}
                label="Rate"
                error={rateError}
                value={rate}
                onChange={(event) => {
                  setRate(event.target.value);
                  setRateError(event.target.value === null);
                }}
                sx={{
                  ...getShakeSx("Rate"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        Number(rate) || "",
                        Number(overlayData.Rate),
                      ),
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                }}
              >
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"UnitType"}
                  label="Unit Type"
                  value={unitTypeId}
                  onChange={(event) => {
                    setUnitTypeId(Number(event.target.value));
                    fillDetails(
                      requestor.Name,
                      name,
                      Number(unitCount),
                      sourceLanguage.Name,
                      targetLanguages,
                      translationType,
                    );
                  }}
                  sx={{
                    minWidth: 160,
                    ...getShakeSx("UnitType"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          unitTypeId || "",
                          overlayData.UnitTypeID || "",
                        ),
                      },
                    },
                  }}
                >
                  {translationUnitTypes.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.Name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"UnitCount"}
                  label="Unit Count"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {unitTypeAbbrev}
                        </InputAdornment>
                      ),
                    },
                  }}
                  error={unitCountError}
                  value={unitCount}
                  onChange={(event) => {
                    setUnitCount(event.target.value);
                    setUnitCountError(event.target.value === null);

                    fillDetails(
                      requestor.Name,
                      name,
                      Number(event.target.value),
                      sourceLanguage.Name,
                      targetLanguages,
                      translationType,
                    );
                  }}
                  sx={{
                    ...getShakeSx("UnitCount"),
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(unitCount) || "",
                          Number(
                            overlayData.UnitCount ?? overlayData.WordCount,
                          ) || "",
                        ),
                      },
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                select
                id={"TranslationStatus"}
                label="Translation Status"
                error={translationStatusError}
                //defaultValue={translationStatus === null ? "Requested" : ""}
                value={translationStatus}
                onChange={(event) => {
                  setTranslationStatus(event.target.value);
                  setTranslationStatusError(event.target.value === null);
                }}
                sx={{
                  ...getShakeSx("TranslationStatus"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        translationStatus || "",
                        overlayData.TranslationStatus,
                      ),
                    },
                  },
                }}
              >
                {overlayData.TranslationStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                slots={{
                  popper: CustomPopper, // Use the custom Popper component
                }}
                multiple
                size="small"
                id="checkboxes-tags-demo"
                options={overlayData.Languages.filter(
                  (lang) => lang.id !== sourceLanguage.id,
                )}
                disableCloseOnSelect
                getOptionLabel={(option) => option.Name}
                //getoptionselected={(option, value) => option.id === value.id} // Add this line
                value={targetLanguages}
                onChange={(event, newValue) => {
                  applyTargetLanguages(
                    newValue.map((item) => ({
                      ...item,
                    })),
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  const isSelected = targetLanguages.some(
                    (lang) => lang.id === option.id,
                  ); // Check if the option is selected
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={isSelected}
                      />
                      {option.Name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      !targetLanguages || targetLanguages.length === 0
                        ? "Target Languages"
                        : "Target Languages: " + targetLanguages.length
                    }
                    placeholder="Type or select from the list"
                    sx={{
                      ...getShakeSx("TargetLanguages"),
                      "& .MuiOutlinedInput-root": {
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,

                            (targetLanguages && targetLanguages.length > 0) ||
                              "",
                            (overlayData.ServiceTargetLanguages &&
                              overlayData.ServiceTargetLanguages.length > 0) ||
                              "",
                          ),
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"LinguistPay"}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  label="Linguist Pay"
                  error={linguistPayError}
                  value={linguistPay}
                  onFocus={() => {
                    if (Number(linguistPay) === 0) {
                      setLinguistPay("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(linguistPay) === 0) {
                      setLinguistPay(0);
                    }
                  }}
                  onChange={(event) => {
                    setLinguistPay(event.target.value);
                    setLinguistPayError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(linguistPay) || "",
                          Number(overlayData.LinguistPay) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AdditionalLinguistCost"}
                  label="Additional Linguist Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={additionalLinguistCostError}
                  value={additionalLinguistCost}
                  onFocus={() => {
                    if (Number(additionalLinguistCost) === 0) {
                      setAdditionalLinguistCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(additionalLinguistCost) === 0) {
                      setAdditionalLinguistCost(0);
                    }
                  }}
                  onChange={(event) => {
                    setAdditionalLinguistCost(event.target.value);
                    setAdditionalLinguistCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(additionalLinguistCost) || "",
                          Number(overlayData.AdditionalLinguistCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalLinguistPay"}
                  label="Total Linguist Pay"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  value={totalLinguistPay}
                  onChange={(event) => {
                    setTotalLinguistPay(event.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalLinguistPay) || "",
                          Number(overlayData.TotalLinguistPay),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AdditionalCost"}
                  label="Additional Costs"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={additionalCostError}
                  value={additionalCost}
                  onFocus={() => {
                    if (Number(additionalCost) === 0) {
                      setAdditionalCost("");
                      //setMileageCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(additionalCost) === 0) {
                      setAdditionalCost(0);
                      //setMileageCost("");
                    }
                  }}
                  onChange={(event) => {
                    setAdditionalCost(event.target.value);
                    setAdditionalCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(additionalCost) || "",
                          Number(overlayData.AdditionalCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"notarizationOrCOTCost"}
                  label="Notarization/COT Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={notarizationOrCOTCostError}
                  value={notarizationOrCOTCost}
                  onChange={(event) => {
                    setNotarizationOrCOTCost(event.target.value);
                    setNotarizationOrCOTCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(notarizationOrCOTCost) || "",
                          Number(overlayData.NotarizationOrCOTCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"DeliveryCost"}
                  label="Delivery Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={deliveryCostError}
                  value={deliveryCost}
                  onChange={(event) => {
                    setDeliveryCost(event.target.value);
                    setDeliveryCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(deliveryCost) || "",
                          Number(overlayData.DeliveryCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"PaymentFees"}
                  label="Payment Fees"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={paymentFeesError}
                  value={paymentFees}
                  onChange={(event) => {
                    setPaymentFees(event.target.value);
                    setPaymentFeesError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(paymentFees) || "",
                          Number(overlayData.PaymentFees) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}
            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <MobileDatePicker
                  disabled={!isEditable}
                  id={"DeliveryDate"}
                  label="Delivery Date"
                  size="small"
                  format="dddd, MMMM DD, YYYY"
                  value={deliveryDate}
                  onChange={(newValue) => {
                    setDeliveryDate(newValue);
                    setDeliveryDateError(newValue === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          (deliveryDate && deliveryDate.format("YYYY-MM-DD")) ||
                            "",
                          overlayData.DeliveryDate,
                        ),
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: deliveryDateError,
                    },
                  }}
                />
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"PaymentType"}
                  label="Payment Type"
                  select
                  error={paymentTypeError}
                  value={paymentType || ""}
                  onChange={(event) => {
                    setPaymentType(event.target.value);
                    setPaymentTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          paymentType || "",
                          overlayData.PaymentType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.PaymentTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {completing ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"DeliveryType"}
                  label="Delivery Type"
                  select
                  error={deliveryTypeError}
                  value={deliveryType || ""}
                  onChange={(event) => {
                    setDeliveryType(event.target.value);
                    setDeliveryTypeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          deliveryType || "",
                          overlayData.DeliveryType || "",
                        ),
                      },
                    },
                  }}
                >
                  {(overlayData.DeliveryTypes || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}

            {showLinguistPayAndOtherCosts ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalCost"}
                  label="Total Cost"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges
                      ? null
                      : totalCostHelperText
                  }
                  error={totalCostError}
                  value={totalCost}
                  onChange={(event) => {
                    setTotalCost(event.target.value);
                    setTotalCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "&.MuiInputBase-root fieldset": {
                        py: 0,
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,

                          Number(totalCost) || "",
                          Number(overlayData.TotalCost),
                          !completing,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            ) : null}

            <Grid
              size={
                showLinguistPayAndOtherCosts ? { xs: 12, md: 6 } : { xs: 12 }
              }
            >
              <TextField
                disabled={!isEditable}
                size="small"
                id={"AccountCharge"}
                label="Account Charge"
                slotProps={{
                  input: {
                    readOnly: !disableAutomaticCalculationOfCharges,
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                helperText={
                  disableAutomaticCalculationOfCharges
                    ? null
                    : accountChargeHelperText
                }
                error={accountChargeError}
                value={accountCharge}
                onChange={(event) => {
                  setAccountCharge(event.target.value);
                  setAccountChargeError(event.target.value === null);

                  const accountChargeValue = parseFloat(event.target.value);
                  const totalCostValue = parseFloat(totalCost);

                  // Prevent division by zero and handle invalid values
                  if (
                    !event.target.value ||
                    accountChargeValue === 0 ||
                    isNaN(accountChargeValue) ||
                    isNaN(totalCostValue)
                  ) {
                    setProfit(0);
                    setMargin(0);
                    return;
                  }

                  const prof = accountChargeValue - totalCostValue;
                  setProfit(prof.toFixed(2));
                  const calculatedMargin = (prof / accountChargeValue) * 100;
                  setMargin(
                    isNaN(calculatedMargin) || !isFinite(calculatedMargin)
                      ? 0
                      : calculatedMargin.toFixed(2),
                  );
                }}
                sx={{
                  ...getShakeSx("AccountCharge"),
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor: getBorderColor(
                        theme,
                        isNewAssignment,

                        Number(accountCharge) || "",
                        Number(overlayData.AccountCharge),
                      ),
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                disabled={isEditable ? !allowDetailsEditing : true}
                size="small"
                id={"Details"}
                label="Details"
                error={detailsError}
                value={details}
                onChange={(event) => {
                  setDetails(event.target.value);
                  setDetailsError(event.target.value === null);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Profit"}
                label="Profit"
                value={profit}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={true}
                size="small"
                id={"Margin"}
                label="Margin"
                value={margin}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "&.MuiInputBase-root fieldset": {
                      py: 0,
                      borderColor:
                        details === null || details === ""
                          ? "gray"
                          : theme.palette.primary.main,
                    },
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography>Options</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <HtmlTooltip
                title={
                  <Box p=".5rem">
                    <Typography
                      color="inherit"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {"Yes: Allow the editing of the details."}
                      {`\n\nNo: Details will be not-editable, and will be filled according to Name, Requestor, and Language.`}
                    </Typography>
                  </Box>
                }
              >
                <FormControlLabel
                  control={<Checkbox />}
                  id={"AllowDetailsEditing"}
                  label="Allow Manual Editing of Details"
                  checked={allowDetailsEditing}
                  onChange={(event) => {
                    setAllowDetailsEditing(event.target.checked);

                    if (!event.target.checked)
                      fillDetails(
                        requestor.Name,
                        name,
                        Number(unitCount),
                        sourceLanguage.Name,
                        targetLanguages,
                        translationType,
                      );
                  }}
                />
              </HtmlTooltip>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <HtmlTooltip
                title={
                  <Box p=".5rem">
                    <Typography
                      color="inherit"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {
                        "Yes: Show: Linguist Pay - Additional Linguist Cost - Additional Costs - Notarization/COT Cost - Delviery Cost - Payment Fees"
                      }
                      {`\n\nNo: Hide: Linguist Pay - Additional Linguist Cost - Additional Costs - Notarization/COT Cost - Delviery Cost - Payment Fees`}
                    </Typography>
                  </Box>
                }
              >
                <FormControlLabel
                  control={<Checkbox />}
                  id={"showLinguistPayAndOtherCosts"}
                  label="Show Linguist Pay & Other Costs"
                  checked={showLinguistPayAndOtherCosts}
                  onChange={(event) => {
                    setShowLinguistPayAndOtherCosts(event.target.checked);
                  }}
                />
              </HtmlTooltip>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <HtmlTooltip
                title={
                  <Box p=".5rem">
                    <Typography
                      color="inherit"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {
                        "Yes: Automatic calculation of Total Cost and Account Charge will be disabled."
                      }
                      {`\n\nNo: Automatic calculation of Total Cost and Account Charge will be enabled.`}
                    </Typography>
                  </Box>
                }
              >
                <FormControlLabel
                  control={<Checkbox />}
                  id={"disableAutomaticCalculationOfCharges"}
                  label="Disable Automatic Calculation of Charges"
                  checked={disableAutomaticCalculationOfCharges}
                  onChange={(event) => {
                    setDisableAutomaticCalculationOfCharges(
                      event.target.checked,
                    );
                  }}
                />
              </HtmlTooltip>
            </Grid>

            {isNewAssignment && targetLanguages.length > 1 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <HtmlTooltip
                  title={
                    <Box p=".5rem">
                      <Typography
                        color="inherit"
                        style={{ whiteSpace: "pre-line" }}
                      >
                        {
                          "Yes: Create one separate service for each target language. Each service will have its calculations based on a single language, with incrementing project numbers."
                        }
                        {`\n\nNo: Create one service with all target languages (current behavior).`}
                      </Typography>
                    </Box>
                  }
                >
                  <FormControlLabel
                    control={<Checkbox />}
                    id={"createServicePerLanguage"}
                    label="Create One Service Per Language"
                    checked={createServicePerLanguage}
                    disabled={targetLanguages.length <= 1}
                    onChange={(event) => {
                      setCreateServicePerLanguage(event.target.checked);
                    }}
                  />
                </HtmlTooltip>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center", // Changed from flex-end to center
              width: "50%",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  progress < 50
                    ? theme.palette.error[600]
                    : progress < 100
                      ? theme.palette.warning[500]
                      : theme.palette.primary.main,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "50%",
              flexDirection: "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      releaseLock();
                      setOverlayName("");
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => {
                    releaseLock();
                    setOverlayName("");
                  }}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="submit"
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Paid Assignment Warning Dialog */}
      <Dialog
        open={showPaidWarning}
        onClose={handlePaidWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: "bold",
          }}
        >
          ⚠️ Paid Assignment Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
              fontWeight: 500,
            }}
          >
            {paidWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePaidWarningCancel}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidWarningConfirm}
            color="warning"
            variant="contained"
            sx={{ borderRadius: "8px" }}
            autoFocus
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default TranslationNewEdit;
